import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText } from '../common';
import { FiSearch, FiMessageSquare, FiArrowRight, FiZap } from 'react-icons/fi';
import routes from '../../utils/routes';

interface CopilotTeaserProps {
  queriesRemaining?: number;
  subscriptionTier?: 'free' | 'pro' | 'premium';
  onUpgradeClick?: () => void;
}

const CopilotTeaser: React.FC<CopilotTeaserProps> = ({
  queriesRemaining = 5,
  subscriptionTier = 'free',
  onUpgradeClick,
}) => {
  const colors = useAppSelector((state) => state.theme.theme);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const sampleInsights = [
    {
      title: 'TCS revenue grew 8.2% YoY with strong deal wins in Q3...',
      company: 'TCS',
    },
    {
      title: 'HDFC Bank NPAs decreased to 1.2%, improving asset quality...',
      company: 'HDFC Bank',
    },
  ];

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(routes.copilot, { state: { initialQuery: searchQuery } });
    } else {
      navigate(routes.copilot);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div
      className="p-5 rounded-2xl mb-6 card-shadow relative overflow-hidden"
      style={{
        backgroundColor: colors.cardBackground || colors.inputBackground,
        border: `1px solid ${colors.borderColor}`,
      }}
    >
      {/* Pro Badge */}
      {subscriptionTier === 'free' && (
        <div
          className="absolute top-4 right-4 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1"
          style={{
            background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
            color: '#fff',
          }}
        >
          PRO
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{
            background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
            boxShadow: colors.shadowGlow,
          }}
        >
          {React.createElement(FiMessageSquare as any, { size: 20, color: '#fff' })}
        </div>
        <div>
          <CText type="M16" color={colors.textColor} style={{ fontWeight: 600 }}>
            Ask Copilot Anything
          </CText>
          <CText type="r12" color={colors.grayScale500}>
            AI-powered stock research assistant
          </CText>
        </div>
      </div>

      {/* Search Box */}
      <div
        className="flex items-center gap-2 p-3 rounded-xl mb-4 cursor-pointer transition-all duration-200 hover:shadow-md"
        style={{
          backgroundColor: colors.backgroundColor,
          border: `1px solid ${colors.borderColor}`,
        }}
        onClick={() => navigate(routes.copilot)}
      >
        {React.createElement(FiSearch as any, { size: 18, color: colors.grayScale500 })}
        <input
          type="text"
          className="flex-1 bg-transparent border-none outline-none text-sm"
          style={{ color: colors.textColor }}
          placeholder="What are the best IT stocks for 2024?"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
        />
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleSearch();
          }}
          className="p-2 rounded-lg transition-all duration-200 hover:opacity-80"
          style={{ backgroundColor: colors.primary }}
        >
          {React.createElement(FiArrowRight as any, { size: 16, color: '#fff' })}
        </button>
      </div>

      {/* Sample Insights */}
      <div className="mb-4">
        <CText type="r12" color={colors.grayScale500} style={{ marginBottom: 8 }}>
          Recent insights from Copilot:
        </CText>
        <div className="space-y-2">
          {sampleInsights.map((insight, idx) => (
            <button
              key={idx}
              onClick={() => navigate(routes.copilot)}
              className="w-full text-left p-3 rounded-lg transition-all duration-200 hover:translate-x-1"
              style={{
                backgroundColor: colors.backgroundColor,
                border: `1px solid ${colors.borderColor}`,
              }}
            >
              <CText type="r12" color={colors.textColor}>
                "{insight.title}"
              </CText>
              <div className="flex items-center justify-between mt-1">
                <CText type="r10" color={colors.grayScale500}>
                  {insight.company}
                </CText>
                <CText type="r10" color={colors.primary} style={{ fontWeight: 500 }}>
                  View full →
                </CText>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Usage Counter & Upgrade CTA */}
      {subscriptionTier === 'free' && (
        <div
          className="flex items-center justify-between p-3 rounded-xl"
          style={{
            backgroundColor: `${colors.primary}10`,
            border: `1px solid ${colors.primary}30`,
          }}
        >
          <div className="flex items-center gap-2">
            {React.createElement(FiZap as any, { size: 16, color: colors.primary })}
            <CText type="r12" color={colors.textColor}>
              <span style={{ fontWeight: 600 }}>{queriesRemaining}</span> free queries remaining this week
            </CText>
          </div>
          <button
            onClick={onUpgradeClick}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 hover:-translate-y-0.5"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
              color: '#fff',
              boxShadow: colors.shadowMedium,
            }}
          >
            Upgrade to Pro
          </button>
        </div>
      )}

      {subscriptionTier !== 'free' && (
        <button
          onClick={() => navigate(routes.copilot)}
          className="w-full py-3 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5"
          style={{
            background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
            color: '#fff',
            boxShadow: colors.shadowMedium,
          }}
        >
          Open Copilot
        </button>
      )}
    </div>
  );
};

export default CopilotTeaser;
